import Swal from "sweetalert2";

/**
 * Common Tailwind CSS class configuration matching your UI theme
 */
const themeConfig = {
  popup:
    "bg-slate-950/95 border border-white/[0.08] backdrop-blur-2xl rounded-2xl shadow-2xl text-slate-100 font-sans p-6",
  title: "text-lg md:text-xl font-extrabold text-white tracking-tight pt-2",
  htmlContainer: "text-xs md:text-sm text-slate-400 mt-2",
  inputLabel:
    "text-xs font-bold uppercase tracking-wider text-slate-300 mb-2 block",
  input:
    "!bg-slate-900/80 !border !border-white/[0.08] !rounded-xl !text-slate-200 !text-sm focus:!outline-none focus:!border-emerald-500/50 !shadow-inner !transition placeholder:!text-slate-600 focus:!ring-0",
  validationMessage:
    "bg-rose-950/40 text-rose-400 border border-rose-500/20 text-xs rounded-xl p-2.5 mt-2",
  confirmButton:
    "px-4 py-2 rounded-xl text-xs md:text-sm font-bold bg-emerald-600 text-white border border-emerald-400/60 shadow-[0_0_15px_rgba(16,185,129,0.3)] hover:bg-emerald-500 transition-all duration-200 transform active:scale-95 cursor-pointer ml-2",
  cancelButton:
    "px-4 py-2 rounded-xl text-xs md:text-sm font-bold bg-transparent text-slate-400 border border-transparent hover:text-slate-200 hover:bg-white/[0.04] transition-all duration-200 transform active:scale-95 cursor-pointer",
  actions: "mt-6 flex items-center gap-2",
};

/**
 * Destructive/Danger confirm button class override (e.g. for reason modal)
 */
const dangerConfirmButtonClass =
  "px-4 py-2 rounded-xl text-xs md:text-sm font-bold bg-rose-600 text-white border border-rose-400/60 shadow-[0_0_15px_rgba(225,29,72,0.3)] hover:bg-rose-500 transition-all duration-200 transform active:scale-95 cursor-pointer ml-2";

/**
 * Helper to standardise base SweetAlert options
 */
const baseSwal = Swal.mixin({
  buttonsStyling: false, // Turn off default SweetAlert button styling
  background: "rgba(2, 6, 23, 0.95)", // Fallback slate-950 background
  color: "#f8fafc",
});

/**
 * Action confirmation with mandatory reason entry
 */
export const confirmWithReason = async ({
  title,
  text,
  confirmText = "Confirm",
  inputLabel = "Reason",
}) => {
  const result = await baseSwal.fire({
    title,
    text,
    icon: "warning",
    iconColor: "#f59e0b", // Amber warning color matching dark mode
    input: "textarea",
    inputLabel,
    inputPlaceholder: "Enter reason...",
    showCancelButton: true,
    confirmButtonText: confirmText,
    cancelButtonText: "Cancel",
    reverseButtons: true,
    customClass: {
      ...themeConfig,
      confirmButton: dangerConfirmButtonClass,
    },
    inputValidator: (value) => {
      if (!value || !value.trim()) {
        return "Reason is required.";
      }
    },
  });

  return result.isConfirmed ? result.value.trim() : null;
};

/**
 * Standard confirmation dialog
 */
export const confirmAction = async ({
  title,
  text,
  confirmText = "Yes",
  icon = "warning",
}) => {
  const iconColors = {
    warning: "#f59e0b",
    error: "#f43f5e",
    info: "#3b82f6",
    question: "#10b981",
  };

  const result = await baseSwal.fire({
    title,
    text,
    icon,
    iconColor: iconColors[icon] || "#f59e0b",
    showCancelButton: true,
    confirmButtonText: confirmText,
    cancelButtonText: "Cancel",
    reverseButtons: true,
    customClass: themeConfig,
  });

  return result.isConfirmed;
};

/**
 * Quick toast/alert for successful actions
 */
export const showSuccess = (message) =>
  baseSwal.fire({
    icon: "success",
    iconColor: "#10b981", // Emerald accent color
    title: "Success",
    text: message,
    timer: 1800,
    showConfirmButton: false,
    customClass: {
      popup: themeConfig.popup,
      title: themeConfig.title,
      htmlContainer: themeConfig.htmlContainer,
    },
  });

/**
 * Error alert modal
 */
export const showError = (message) =>
  baseSwal.fire({
    icon: "error",
    iconColor: "#f43f5e", // Rose accent color
    title: "Error",
    text: message,
    confirmButtonText: "Dismiss",
    customClass: {
      ...themeConfig,
      confirmButton: dangerConfirmButtonClass,
    },
  });
